import React, { useState, useEffect, useRef } from 'react';
import { Card, Input } from 'antd';
import { useTranslation } from 'react-i18next';

interface NotesProps {
  initialNotes?: string;
  onDataChange?: (notes: string) => void;
}

const Notes: React.FC<NotesProps> = ({ initialNotes, onDataChange }) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');

  // Track if user has made changes (dirty flag to prevent unwanted resets)
  const isDirtyRef = useRef(false);
  // Track the last applied initialNotes to avoid re-applying same data
  const appliedInitialRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if user has made edits
    if (isDirtyRef.current) return;
    if (initialNotes == null) return;

    // Skip if this is the same data we already applied
    if (appliedInitialRef.current === initialNotes) return;

    appliedInitialRef.current = initialNotes;
    setNotes(initialNotes);
  }, [initialNotes]);

  // Notify parent of data changes (use stable callback reference)
  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  useEffect(() => {
    onDataChangeRef.current?.(notes);
  }, [notes]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    isDirtyRef.current = true;
    setNotes(e.target.value);
  };

  return (
    <Card title={t('idea_calc.notes.title')} variant="outlined">
      <Input.TextArea
        rows={5}
        placeholder={t('idea_calc.notes.placeholder')}
        value={notes}
        onChange={handleChange}
      />
    </Card>
  );
};

export default React.memo(Notes);
